import { Box, List, ListItem, Stack, TableCell, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { RequestProps } from '../../../store/types'
import { useGetUsersQuery } from '../../../store/redux/reducers/usersSlice'
import { useSelector } from 'react-redux'
import {
  getCurrentUser,
  getIsLeader,
  getIsManagement,
  getIsProjectLeader,
} from '../../../store/redux/reducers/currentUserSlice'
import ReportsActionMenu from '../../Popup/ReportsActionMenu'
import { generatePath, useNavigate } from 'react-router-dom'
import navigation from '../../../config/navigation'
import { useTranslation } from 'react-i18next'
import AvatarComponent from '../../avatar/AvatarComponent'
import DataTable from '../DataTable'
import ReportPopupData from '../../../store/types/utils/ReportPopupUtil'
import { StickyCell } from '../../../styles/WorklogTableStyles'
import { Column, FullName, Row } from '../../../store/types/utils/DataTableUtils'
import LocalStorageTypes from '../../../store/types/enums/LocalStorageTypes'
import SessionStorageKeys from '../../../store/types/enums/SessionStorageKeys'
import usePersistedPage from '../../../store/utils/usePersistedPage'

const ReportsUsersTable = ({
  searchInput,
  concreteUser,
  updateSelectedData,
  rowsPerPage,
  setRowsPerPage,
}: {
  searchInput: string
  concreteUser: boolean
  updateSelectedData: (selectedIds: ReportPopupData[]) => void
  rowsPerPage: number
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>
}) => {
  const [page, setPage] = usePersistedPage(SessionStorageKeys.ReportsUsersTablePage)
  const currentUser = useSelector(getCurrentUser)
  const isLeader = useSelector(getIsLeader)
  const isProjectLeader = useSelector(getIsProjectLeader)
  const isManagement = useSelector(getIsManagement)

  const navigate = useNavigate()
  const defaultPropsLeader: RequestProps = {
    page: page,
    size: rowsPerPage,
    ...(isLeader ? { 'query:leaders.id,id': currentUser?.id } : {}),
    ...(!isLeader && isProjectLeader ? { 'projects.leaders.leader.id': currentUser?.id } : {}),
  }

  const [requestProps, setRequestProps] = useState<RequestProps>(
    (isProjectLeader || isLeader) && !isManagement
      ? defaultPropsLeader
      : {
          page: page,
          size: rowsPerPage,
        }
  )

  useEffect(() => {
    setRequestProps(prevRequestProps => ({
      ...prevRequestProps,
      'query:firstName,lastName': searchInput,
      page: 0,
    }))
    setPage(0)
  }, [searchInput, setPage])

  const { data: usersResponse, isLoading } = useGetUsersQuery(requestProps)
  const { t } = useTranslation()

  const columns: Column[] = [
    { id: 'firstName', label: t('components.table.header.employee') },
    { id: 'leaderLastNames', label: t('page.users.table.leader') },
    { id: 'employeeStatus', label: t('components.table.header.status') },
    { id: 'action', label: t('components.table.header.action') },
  ]

  enum employeeStatus {
    ACTIVE = 'ACTIVE',
    BLOCKED = 'BLOCKED',
    DELETED = 'DELETED',
  }

  const statusTranslationMapping = {
    [employeeStatus.ACTIVE]: 'page.usersProfile.statuses.ACTIVE',
    [employeeStatus.DELETED]: 'page.usersProfile.statuses.DELETED',
    [employeeStatus.BLOCKED]: 'page.usersProfile.statuses.BLOCKED',
  }

  const renderTile = (user: Row, labelId: string, checkbox?: React.ReactNode) => {
    const navigateString = currentUser?.id !== user.id ? `${navigation.users}/${user.id}` : `${navigation.myProfile}`

    return (
      <>
        <Stack direction="row" justifyContent="space-between" width="100%" key={labelId}>
          <Stack direction="row" alignItems="center">
            {checkbox && checkbox}
            <Box
              sx={{
                border: '1px solid',
                borderRadius: 1,
                padding: '5px',
                borderColor: 'background.elevation8',
              }}
            >
              <Typography variant="body2" color="textSecondary">
                {t(`statuses.${user.employeeStatus.toLowerCase()}`)}
              </Typography>
            </Box>
          </Stack>
          <ReportsActionMenu
            navigationPath={generatePath(navigation.userProfile, { id: user.id })}
            label={`${user.firstName} ${user.lastName}`}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1} marginBottom="5px" marginLeft="2px">
          <AvatarComponent
            extension={user.extension}
            avatar={user.avatar}
            alt={`${user.firstName}-${user.lastName}-profile-picture`}
            size={36}
          />
          <Stack direction="column" justifyContent="space-between">
            <Typography variant="h6" sx={{ cursor: 'pointer' }} onClick={() => navigate(navigateString)}>
              {user.firstName} {user.lastName}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="column" justifyContent="space-between" marginTop="8px" marginLeft="47px">
          {user.leaders && user.leaders.length > 0 && (
            <Box width="100%" marginTop="8px">
              <Typography variant="body2" color="textSecondary" fontWeight={580}>
                {t('page.users.filtering.leaders')}:
              </Typography>
              <List sx={{ py: '2px' }}>
                {user.leaders.map((leader: FullName, index: number) => (
                  <ListItem key={index} sx={{ py: '4px' }}>
                    <Typography variant="body2">- {`${leader.firstName} ${leader.lastName}`}</Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Stack>
      </>
    )
  }

  const renderRow = (user: Row) => (
    <>
      <TableCell sx={{ pl: concreteUser ? 3 : 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
          <AvatarComponent
            avatar={user.avatar}
            extension={user.extension}
            size={24}
            alt={`${user.firstName}-${user.lastName}-user-avatar`}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography fontWeight={700}>{user.firstName}</Typography>
            <Typography>{user.lastName}</Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        {' '}
        {user.leaders && user.leaders.length > 0
          ? user.leaders.map((leader: FullName) => `${leader.firstName} ${leader.lastName}`).join(', ')
          : '-'}
      </TableCell>
      <TableCell>{t(statusTranslationMapping[user.employeeStatus as keyof typeof employeeStatus])}</TableCell>
      <StickyCell
        align="center"
        sx={{
          borderLeft: '2px solid',
          borderLeftColor: 'background.elevation8',
          right: 0,
          '&:hover': {
            cursor: 'auto',
          },
        }}
      >
        <div className="ignore-row-click">
          <ReportsActionMenu
            navigationPath={generatePath(navigation.userProfile, { id: user.id })}
            label={user.firstName + ' ' + user.lastName}
          />
        </div>
      </StickyCell>
    </>
  )

  return (
    <DataTable
      unsortableColumns={[{ id: 'action', label: t('components.table.header.action') }]}
      isReportsView={true}
      renderTileCheckbox={true}
      renderTileContent={renderTile}
      singleSelection={concreteUser}
      updateSelectedData={updateSelectedData}
      columns={columns}
      data={usersResponse?.content || []}
      renderRow={renderRow}
      totalElements={usersResponse?.totalElements || 0}
      setRequestProps={setRequestProps}
      translationForNotFound={'page.users.table.noUserFound'}
      page={page}
      setPage={setPage}
      commonRowsPerPage={rowsPerPage}
      setCommonRowsPerPage={setRowsPerPage}
      isLoading={isLoading}
      localStorageRowsName={LocalStorageTypes.ReportsTableRows}
    />
  )
}

export default ReportsUsersTable
